import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Vote, Trophy, Users, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface RecentMatch {
  id: string;
  match_date: string;
  score_a: number;
  score_b: number;
  player_a: { id: string; name: string; avatar: string | null };
  player_b: { id: string; name: string; avatar: string | null };
}

interface VoteCount {
  player_id: string;
  count: number;
}

export const FanPolls = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<RecentMatch[]>([]);
  const [voteCounts, setVoteCounts] = useState<Map<string, Map<string, number>>>(new Map());
  const [userVotes, setUserVotes] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentMatches();
  }, []);

  useEffect(() => {
    if (user && matches.length > 0) {
      fetchUserVotes();
      fetchVoteCounts();
    }
  }, [user, matches]);

  const fetchRecentMatches = async () => {
    const { data } = await supabase
      .from('matches')
      .select(`
        id, match_date, score_a, score_b,
        player_a:players!matches_team_a_fkey(id, name, avatar),
        player_b:players!matches_team_b_fkey(id, name, avatar)
      `)
      .eq('status', 'completed')
      .not('team_a', 'is', null)
      .not('team_b', 'is', null)
      .order('match_date', { ascending: false })
      .limit(5);

    if (data) {
      setMatches(data.map(m => ({
        ...m,
        player_a: m.player_a as any,
        player_b: m.player_b as any
      })));
    }
    setLoading(false);
  };

  const fetchUserVotes = async () => {
    if (!user) return;
    const matchIds = matches.map(m => m.id);
    const { data } = await supabase
      .from('fan_polls')
      .select('match_id, voted_player_id')
      .eq('user_id', user.id)
      .in('match_id', matchIds);

    if (data) {
      const voteMap = new Map();
      data.forEach(v => voteMap.set(v.match_id, v.voted_player_id));
      setUserVotes(voteMap);
    }
  };

  const fetchVoteCounts = async () => {
    const matchIds = matches.map(m => m.id);
    const { data } = await supabase
      .from('fan_polls')
      .select('match_id, voted_player_id')
      .in('match_id', matchIds);

    if (data) {
      const countsMap = new Map<string, Map<string, number>>();
      
      data.forEach(vote => {
        if (!countsMap.has(vote.match_id)) {
          countsMap.set(vote.match_id, new Map());
        }
        const matchVotes = countsMap.get(vote.match_id)!;
        matchVotes.set(vote.voted_player_id, (matchVotes.get(vote.voted_player_id) || 0) + 1);
      });

      setVoteCounts(countsMap);
    }
  };

  const submitVote = async (matchId: string, playerId: string) => {
    if (!user) {
      toast.error('ভোট দিতে লগইন করুন');
      return;
    }

    if (userVotes.has(matchId)) {
      toast.info('আপনি ইতিমধ্যে ভোট দিয়েছেন');
      return;
    }

    setVoting(matchId);
    try {
      const { error } = await supabase
        .from('fan_polls')
        .insert({
          match_id: matchId,
          user_id: user.id,
          voted_player_id: playerId
        });

      if (error) throw error;

      setUserVotes(prev => {
        const newMap = new Map(prev);
        newMap.set(matchId, playerId);
        return newMap;
      });

      // Update local vote counts
      setVoteCounts(prev => {
        const newMap = new Map(prev);
        if (!newMap.has(matchId)) {
          newMap.set(matchId, new Map());
        }
        const matchVotes = newMap.get(matchId)!;
        matchVotes.set(playerId, (matchVotes.get(playerId) || 0) + 1);
        return newMap;
      });

      toast.success('ভোট সফল হয়েছে!');
    } catch (error) {
      toast.error('ভোট দিতে সমস্যা হয়েছে');
    } finally {
      setVoting(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 animate-pulse">
        <div className="h-40 bg-muted rounded" />
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-gradient-to-br from-card/80 to-card border-primary/20">
      <div className="flex items-center gap-2 mb-4">
        <Vote className="w-5 h-5 text-primary" />
        <h3 className="font-bold text-lg">Man of the Match Vote</h3>
      </div>

      {matches.length === 0 ? (
        <div className="text-center text-muted-foreground py-8">
          <Vote className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>কোন সাম্প্রতিক ম্যাচ নেই</p>
        </div>
      ) : (
        <div className="space-y-4">
          {matches.slice(0, 3).map(match => {
            const hasVoted = userVotes.has(match.id);
            const userVotedFor = userVotes.get(match.id);
            const matchVotes = voteCounts.get(match.id) || new Map();
            const totalVotes = Array.from(matchVotes.values()).reduce((a, b) => a + b, 0);
            const votesA = matchVotes.get(match.player_a.id) || 0;
            const votesB = matchVotes.get(match.player_b.id) || 0;

            return (
              <div key={match.id} className="bg-background/50 rounded-lg p-3 border border-border/50">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                  <span>{format(new Date(match.match_date), 'dd MMM yyyy')}</span>
                  <span className="font-medium text-foreground">
                    {match.score_a} - {match.score_b}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Player A */}
                  <button
                    onClick={() => submitVote(match.id, match.player_a.id)}
                    disabled={voting === match.id || hasVoted}
                    className={`flex-1 p-2 rounded-lg transition-all ${
                      userVotedFor === match.player_a.id
                        ? 'bg-primary/20 border-2 border-primary'
                        : hasVoted 
                          ? 'bg-muted/30 opacity-60 cursor-not-allowed'
                          : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={match.player_a.avatar || ''} />
                        <AvatarFallback>{match.player_a.name[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-medium truncate w-full text-center">
                        {match.player_a.name.split(' ')[0]}
                      </span>
                      {hasVoted && totalVotes > 0 && (
                        <div className="w-full mt-1">
                          <Progress value={(votesA / totalVotes) * 100} className="h-1" />
                          <span className="text-[10px] text-muted-foreground">
                            {Math.round((votesA / totalVotes) * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </button>

                  <div className="px-2 text-xs text-muted-foreground">
                    <Users className="w-4 h-4 mx-auto mb-1" />
                    {totalVotes}
                  </div>

                  {/* Player B */}
                  <button
                    onClick={() => submitVote(match.id, match.player_b.id)}
                    disabled={voting === match.id || hasVoted}
                    className={`flex-1 p-2 rounded-lg transition-all ${
                      userVotedFor === match.player_b.id
                        ? 'bg-primary/20 border-2 border-primary'
                        : hasVoted 
                          ? 'bg-muted/30 opacity-60 cursor-not-allowed'
                          : 'bg-muted/50 hover:bg-muted border-2 border-transparent'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={match.player_b.avatar || ''} />
                        <AvatarFallback>{match.player_b.name[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-xs font-medium truncate w-full text-center">
                        {match.player_b.name.split(' ')[0]}
                      </span>
                      {hasVoted && totalVotes > 0 && (
                        <div className="w-full mt-1">
                          <Progress value={(votesB / totalVotes) * 100} className="h-1" />
                          <span className="text-[10px] text-muted-foreground">
                            {Math.round((votesB / totalVotes) * 100)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
